import React, { useState } from 'react';
import { Send, Plus, Check } from 'lucide-react';
import { gymSubmissionsApi } from '../../lib/api';

export const SubmitGymForm = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    gym_name: '', city: '', state: '', contact_email: '', contact_name: '',
    website: '', description: '', is_hyrox_affiliated: false, phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.gym_name || !form.city || !form.state || !form.contact_email || !form.contact_name) return;
    setSubmitting(true);
    setError('');
    try {
      await gymSubmissionsApi.submit(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-8 bg-green-500/5 border border-green-500/20 p-6 text-center" data-testid="gym-submission-success">
        <Check className="w-8 h-8 text-green-500 mx-auto mb-3" />
        <h3 className="text-white font-heading text-lg mb-2">Gym Submitted Successfully</h3>
        <p className="text-zinc-400 text-sm">We&apos;ll review your submission and get back to you. Thank you for helping grow the HYROX community!</p>
      </div>
    );
  }

  const fields = [
    { k: 'gym_name', l: 'Gym Name *', p: 'e.g. Iron Athletics', req: true },
    { k: 'contact_name', l: 'Your Name *', p: 'Full name', req: true },
    { k: 'contact_email', l: 'Email *', p: 'your@email.com', req: true, type: 'email' },
    { k: 'phone', l: 'Phone', p: '(optional)' },
    { k: 'city', l: 'City *', p: 'e.g. Austin', req: true },
    { k: 'state', l: 'State *', p: 'e.g. TX', req: true },
    { k: 'website', l: 'Website', p: 'https:// (optional)' },
  ];

  return (
    <div className="mb-8" data-testid="submit-gym-container">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-full py-5 bg-[#e8ff00]/5 border-2 border-dashed border-[#e8ff00]/30 hover:border-[#e8ff00]/60 hover:bg-[#e8ff00]/10 text-[#e8ff00] transition-all flex items-center justify-center gap-3 font-heading text-base uppercase tracking-wider"
          data-testid="submit-gym-toggle"
        >
          <Plus className="w-5 h-5" /> Own a Gym? Add It to Our Directory — Free
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="bg-zinc-900/80 border border-[#e8ff00]/20 p-6" data-testid="submit-gym-form">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-heading text-lg flex items-center gap-2">
              <Send className="w-4 h-4 text-[#e8ff00]" /> Submit Your Gym
            </h3>
            <button type="button" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-wider font-heading">
              Close
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {fields.map((f) => (
              <div key={f.k}>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">{f.l}</label>
                <input
                  type={f.type || 'text'}
                  value={form[f.k]}
                  onChange={(e) => set(f.k, e.target.value)}
                  placeholder={f.p}
                  required={f.req}
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-600 focus:border-[#e8ff00] focus:outline-none transition-colors"
                  data-testid={`gym-field-${f.k}`}
                />
              </div>
            ))}
          </div>

          <div className="mb-4">
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Tell us about your gym and HYROX training program..."
              rows={3}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-600 focus:border-[#e8ff00] focus:outline-none transition-colors resize-none"
              data-testid="gym-field-description"
            />
          </div>

          <label className="flex items-center gap-3 mb-5 cursor-pointer group" data-testid="gym-field-affiliated">
            <div
              className={`w-5 h-5 border flex items-center justify-center transition-colors ${
                form.is_hyrox_affiliated ? 'bg-[#e8ff00] border-[#e8ff00]' : 'border-zinc-600 group-hover:border-zinc-400'
              }`}
              onClick={() => set('is_hyrox_affiliated', !form.is_hyrox_affiliated)}
            >
              {form.is_hyrox_affiliated && <Check className="w-3 h-3 text-black" />}
            </div>
            <span className="text-zinc-400 text-sm">This gym is an official HYROX Training Club or Performance Centre</span>
          </label>

          {error && <p className="text-red-500 text-sm mb-4" data-testid="gym-submit-error">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-[#e8ff00] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="gym-submit-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Gym for Review'}
            {!submitting && <Send className="w-4 h-4" />}
          </button>
        </form>
      )}
    </div>
  );
};
